import { Box, Button, Chip, Typography } from "@mui/material";
import {
	ArrowBack,
	ArrowForward,
	Check,
	CheckCircle,
	SkipNext,
	Delete,
	Save,
	RestartAlt,
	Warning,
} from "@mui/icons-material";
import PortalTooltip from "../../../../helpers/PortalTooltip";

// ============================================================================
// Types
// ============================================================================

export interface WizardNavigationProps {
	isFirstStep: boolean;
	isLastStep: boolean;
	canProceed: boolean;
	/** Whether Previous button should be enabled (for unified issue+step navigation) */
	canClickPrevious?: boolean;
	disabledReason?: string;
	onPrevious: () => void;
	onNext: () => void;
	onFinish?: () => void;
	// Issue props (needed for Skip buttons)
	issueCount?: number;
	currentIssueIndex?: number;
	unskippedCount?: number;
	isCurrentIssueSkipped?: boolean;
	onSkipIssue?: () => void;
	onSkipAllIssues?: () => void;
	// MissingInvalidData step actions
	onDeleteJob?: () => void;
	onSaveChanges?: () => void;
	// Start Over button (for embedded wizard mode)
	onStartOver?: () => void;
	// GeoTypeSelection status chips (for bottom bar)
	geoTypeStatus?: {
		assignedCount: number;
		totalActiveGroups: number;
		excludedCount: number;
		unassignedCount: number;
	};
}

// ============================================================================
// Component
// ============================================================================

export function WizardNavigation({
	isFirstStep,
	isLastStep,
	canProceed,
	canClickPrevious,
	disabledReason,
	onPrevious,
	onNext,
	onFinish,
	// Issue props (for Skip buttons)
	issueCount = 0,
	unskippedCount = 0,
	isCurrentIssueSkipped = false,
	onSkipIssue,
	onSkipAllIssues,
	// MissingInvalidData step actions
	onDeleteJob,
	onSaveChanges,
	// Start Over button
	onStartOver,
	// GeoTypeSelection status chips
	geoTypeStatus,
}: WizardNavigationProps) {
	const hasIssues = issueCount > 0;

	// Use canClickPrevious if provided, otherwise fall back to isFirstStep check
	const isPreviousDisabled = canClickPrevious !== undefined ? !canClickPrevious : isFirstStep;

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				width: "100%",
			}}
		>
			{/* Left: Start Over + Previous Step Button + Delete Job */}
			<Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start" }}>
				<Button
					data-testid="prev-step-button"
					variant="contained"
					color="primary"
					disabled={isPreviousDisabled}
					onClick={onPrevious}
					startIcon={<ArrowBack />}
				>
					Previous
				</Button>
				{/* Delete Job Button (MissingInvalidData step only) */}
				{onDeleteJob && (
					<Button
						data-testid="delete-job-button"
						variant="contained"
						color="error"
						onClick={onDeleteJob}
						startIcon={<Delete />}
					>
						Delete Job
					</Button>
				)}
				{/* Start Over Button (only shown when onStartOver is provided) */}
				{onStartOver && (
					<Button data-testid="start-over-button" variant="text" onClick={onStartOver} startIcon={<RestartAlt />}>
						Start Over
					</Button>
				)}
			</Box>

			{/* Center: GeoType status chips, skipped indicator, or validation warning */}
			<Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 1 }}>
				{/* GeoTypeSelection status chips */}
				{geoTypeStatus && (
					<>
						{/* Assigned chip */}
						<Chip
							size="small"
							label={`${geoTypeStatus.assignedCount}/${geoTypeStatus.totalActiveGroups} assigned`}
							color={geoTypeStatus.assignedCount === geoTypeStatus.totalActiveGroups ? "success" : "warning"}
							icon={geoTypeStatus.assignedCount === geoTypeStatus.totalActiveGroups ? <CheckCircle /> : undefined}
						/>
						{/* Excluded chip (only if > 0) */}
						{geoTypeStatus.excludedCount > 0 && (
							<Chip size="small" label={`${geoTypeStatus.excludedCount} excluded`} variant="outlined" />
						)}
					</>
				)}
				{/* Skipped indicator (for validation steps) */}
				{!geoTypeStatus && isCurrentIssueSkipped && (
					<Chip data-testid="skipped-indicator" label="This issue has been skipped" color="warning" />
				)}
				{/* Generic validation warning (for other steps) */}
				{!geoTypeStatus && !isCurrentIssueSkipped && disabledReason && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "warning.main" }}>
						<Warning fontSize="small" />
						<Typography variant="body2" color="warning.main">
							{disabledReason}
						</Typography>
					</Box>
				)}
			</Box>

			{/* Right: Skip + Next/Finish Buttons */}
			<Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
				{/* Skip All Button */}
				{unskippedCount > 1 && (
					<Button size="small" variant="text" onClick={onSkipAllIssues}>
						Ignore All ({unskippedCount})
					</Button>
				)}
				{/* Skip Button */}
				{hasIssues && !isCurrentIssueSkipped && onSkipIssue && (
					<Button data-testid="skip-issue-button" variant="outlined" onClick={onSkipIssue} startIcon={<SkipNext />}>
						Ignore
					</Button>
				)}

				{/* Save Changes Button (MissingInvalidData step only) */}
				{onSaveChanges && (
					<Button
						data-testid="save-changes-button"
						variant="contained"
						color="primary"
						sx={{ bgcolor: "success.main", "&:hover": { bgcolor: "success.dark" } }}
						onClick={onSaveChanges}
						startIcon={<Save />}
					>
						Save Changes
					</Button>
				)}

				{/* Next / Finish Button */}
				{isLastStep ? (
					<Button
						data-testid="finish-button"
						variant="contained"
						color="primary"
						disabled={!canProceed}
						onClick={onFinish}
						endIcon={<Check />}
					>
						Review Jobs
					</Button>
				) : (
					<PortalTooltip title={!canProceed && disabledReason ? disabledReason : ""}>
						<Box data-testid="next-button-wrapper" component="span">
							<Button
								data-testid="next-step-button"
								variant="contained"
								color="primary"
								disabled={!canProceed}
								onClick={onNext}
								endIcon={<ArrowForward />}
							>
								Next
							</Button>
						</Box>
					</PortalTooltip>
				)}
			</Box>
		</Box>
	);
}
